import { Box, ButtonBase, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useColorMode } from "src/design/ColorModeProvider";
import { elevation, radius } from "src/design/tokens";
import { fontStretch } from "src/design/typography";
import StatusChip, {
  StatusChipStatus,
} from "src/grading/components/StatusChip/StatusChip";

export interface ClassCardProps {
  id: string;
  name: string;
  gradeLabel: string;
  year: string | number;
  studentCount?: number;
  testedCount?: number;
  status: StatusChipStatus;
  nextSessionLabel?: string;
  /**
   * `compact` (default): the Today card, a 64 px progress ring beside the text.
   * `full`: the Classes page card, tricolor top rule, big student count and a progress bar.
   */
  variant?: "compact" | "full";
}

/** Class summary card; the whole card links to the class page. */
export const ClassCard = ({
  id,
  name,
  gradeLabel,
  year,
  studentCount,
  testedCount,
  status,
  nextSessionLabel,
  variant = "compact",
}: ClassCardProps) => {
  const { t } = useTranslation();
  const { mode } = useColorMode();
  const hasProgress = testedCount !== undefined;
  const total = studentCount ?? 0;
  const pct =
    hasProgress && total > 0
      ? Math.min(100, Math.round((testedCount / total) * 100))
      : 0;
  const testedLine = hasProgress
    ? t("classes.testedOf", {
        tested: testedCount,
        count: total,
        defaultValue: "{{tested}} of {{count}} tested",
      })
    : undefined;

  const baseSx = {
    width: "100%",
    textAlign: "left" as const,
    bgcolor: "background.paper",
    boxShadow: elevation.card[mode],
    color: "text.primary",
    transition: "transform .2s",
    "&:hover": { transform: "translateY(-2px)" },
    "&.Mui-focusVisible": {
      outline: (theme: { palette: { focusRing: string } }) =>
        `2px solid ${theme.palette.focusRing}`,
      outlineOffset: "2px",
    },
  };

  if (variant === "full") {
    return (
      <ButtonBase
        component={Link}
        to={`/grading/classes/${id}`}
        data-variant="full"
        sx={{
          ...baseSx,
          display: "flex",
          flexDirection: "column",
          alignItems: "stretch",
          overflow: "hidden",
          borderRadius: "20px",
        }}
      >
        <Box aria-hidden sx={{ height: 6, display: "flex" }}>
          <Box sx={{ flex: 1, bgcolor: "primary.main" }} />
          <Box sx={{ flex: 1, bgcolor: "surfaceAlt" }} />
          <Box sx={{ flex: 1, bgcolor: "error.main" }} />
        </Box>
        <Box
          sx={{ p: 2.5, display: "flex", flexDirection: "column", gap: 1.75 }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              gap: 1.25,
            }}
          >
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
              <Typography
                sx={{
                  fontSize: 18,
                  fontWeight: 900,
                  lineHeight: 1.1,
                  fontStretch: fontStretch.heading,
                }}
              >
                {name}
              </Typography>
              <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
                {`${gradeLabel} · ${year}`}
              </Typography>
            </Box>
            <StatusChip status={status} />
          </Box>
          {studentCount !== undefined ? (
            <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.75 }}>
              <Typography variant="stat" component="span">
                {studentCount}
              </Typography>
              <Typography
                component="span"
                sx={{ fontSize: 13, fontWeight: 600, color: "text.secondary" }}
              >
                {hasProgress
                  ? t("classes.studentsTestedWindow", {
                      tested: testedCount,
                      defaultValue: "students · {{tested}} tested this window",
                    })
                  : t("classes.studentsWord", { defaultValue: "students" })}
              </Typography>
            </Box>
          ) : null}
          {hasProgress ? (
            <Box
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={pct}
              aria-label={testedLine}
              sx={{
                height: 8,
                borderRadius: `${radius.pill}px`,
                bgcolor: "track",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  height: "100%",
                  width: `${pct}%`,
                  bgcolor: "primary.main",
                  borderRadius: `${radius.pill}px`,
                }}
              />
            </Box>
          ) : null}
          {nextSessionLabel ? (
            <Typography
              sx={{ fontSize: 13, fontWeight: 600, color: "text.secondary" }}
            >
              {nextSessionLabel}
            </Typography>
          ) : null}
        </Box>
      </ButtonBase>
    );
  }

  return (
    <ButtonBase
      component={Link}
      to={`/grading/classes/${id}`}
      data-variant="compact"
      sx={{
        ...baseSx,
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-start",
        gap: 2,
        p: 2.25,
        borderRadius: `${radius.card}px`,
      }}
    >
      {hasProgress ? (
        <Box
          sx={(theme) => ({
            flex: "0 0 auto",
            width: 64,
            height: 64,
            borderRadius: "50%",
            background: `conic-gradient(${theme.palette.primary.main} ${pct * 3.6}deg, ${theme.palette.track} 0)`,
            display: "grid",
            placeItems: "center",
          })}
        >
          <Box
            sx={{
              width: 50,
              height: 50,
              borderRadius: "50%",
              bgcolor: "background.paper",
              display: "grid",
              placeItems: "center",
              fontSize: 15,
              fontWeight: 800,
              fontStretch: fontStretch.condensed,
            }}
          >
            {`${pct}%`}
          </Box>
        </Box>
      ) : null}
      <Box
        sx={{
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          gap: 0.75,
        }}
      >
        <Typography sx={{ fontSize: 16, fontWeight: 800, lineHeight: 1.15 }}>
          {name}
        </Typography>
        <Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>
          {testedLine
            ? `${gradeLabel} · ${testedLine}`
            : studentCount !== undefined
              ? `${gradeLabel} · ${t("classes.studentCount", {
                  count: studentCount,
                  defaultValue: "{{count}} students",
                })}`
              : `${gradeLabel} · ${year}`}
        </Typography>
        <Box
          sx={{
            display: "flex",
            gap: 1,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <StatusChip status={status} />
          {nextSessionLabel ? (
            <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
              {nextSessionLabel}
            </Typography>
          ) : null}
        </Box>
      </Box>
    </ButtonBase>
  );
};

export default ClassCard;
